/* eslint-disable consistent-return */
/* eslint-disable import/no-cycle */
import { useState } from 'react';
import axios, { AxiosError } from 'axios';
import { useAuth } from './useAuth';

export const baseUrl = process.env.API_BASE_URL;

// Module-level flag to prevent multiple 401 logouts racing
let isLoggingOut = false;

const useAxios = (contentType = 'application/json') => {
  const { logout } = useAuth();
  const [apiLoading, setLoading] = useState(false);
  const [apiError, setError] = useState(null);

  const getSourceType = () => {
    return localStorage.getItem('source');
  };

  const instance = axios.create({
    baseURL: baseUrl,
    headers: {
      'Content-Type': contentType,
      timeout: 1000,
    },
    withCredentials: true,
    // .. other options
  });

  instance.interceptors.request.use(async (config) => {
    // config.headers.Authorization = `Bearer ${token}`; // Enable it when APIs are ready
    // config.headers.Authorization = undefined;
    // // config.headers["X-REQUESTID"] = uniqueId;

    // // encrypting the request body
    // if (
    //   config.method === 'post'
    //   || config.method === 'put'
    //   || config.method === 'patch'
    //   || config.method === 'delete'
    // ) {
    //   const payload = config.data;
    //   if (payload) {
    //     // config.headers["content-type"] = "application/json";
    //     config.data = payload;
    //   }
    // }

    // const token = localStorage.getItem('authToken');
    // config.headers.Authorization = config.url?.includes('user_authentication') ? localStorage.getItem('microsoftToken') : token ? `Bearer ${token}` : '';
    // No need to manually set Authorization header - cookies are sent automatically
    // For SSO, still need to send the Microsoft token
    if (config.url?.includes('user_authentication') || config.url?.includes('cookie_user_authentication')) {
      const msToken = localStorage.getItem('microsoftToken');
      if (msToken) {
        config.headers.Authorization = msToken;
      }
    }

    if (config.url?.includes('update_cwid_info')) {
      const msAccessToken = localStorage.getItem('microsoftAccessToken');
      if (msAccessToken) {
        config.headers.Authorization = `Bearer ${msAccessToken}`;
      }
    }

    return config;
  });

  const handleSessionExpired = () => {
    if (isLoggingOut || window.location.pathname === '/login') return false;
    isLoggingOut = true;
    sessionStorage.setItem('sessionExpired', 'true');
    logout({ redirect: window.location.pathname });
    setTimeout(() => { isLoggingOut = false; }, 3000);
    return true;
  };
  // Track if we're currently refreshing to avoid multiple refresh calls
  let isRefreshing = false;
  let failedQueue: any[] = [];
  const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach(prom => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    failedQueue = [];
  };

  instance.interceptors.response.use(
    (response: any) => {
      if (response.data?.errors && response.data?.errors.length > 0 && (response.data?.errors[0] === 'ERROR: Token Expired' || response.data?.errors[0] === 'ERROR: Token mismatch in \'directory_token_validation\'')) {
        // handleSessionExpired();
        return response;
      }
      return response;
    },
    async (error) => {
      const originalRequest = error.config;
      // Skip refresh for requests marked with skipAuthRefresh
      if (originalRequest?.skipAuthRefresh) {
        return Promise.reject(error);
      }
      // If error is 401 and we haven't tried to refresh yet
      if (error?.response?.status === 401 && !originalRequest.retry) {
        if (isRefreshing) {
          // If already refreshing, queue this request
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(() => {
            return instance(originalRequest);
          }).catch(err => {
            return Promise.reject(err);
          });
        }

        originalRequest.retry = true;
        isRefreshing = true;
        if (getSourceType() !== null) {
          try {
            // Call refresh token endpoint
            const refreshResponse = await instance.post('user-manager/refresh_access_token', getSourceType(), {
              withCredentials: true,
              skipAuthRefresh: true
            } as any);

            if ([200, 201].includes(refreshResponse.status)) {
              if (refreshResponse.data.errors && refreshResponse.data.errors.length > 0) {
                // If 200 but errors exist, call the API again (retry refresh)
                await instance.post('user-manager/refresh_access_token', getSourceType(), {
                  withCredentials: true,
                  skipAuthRefresh: true
                } as any);
                // After retry, assume success or handle further
                processQueue(null, null);
                isRefreshing = false;
                return instance(originalRequest);
              } else {
                // Token refreshed successfully, process queued requests
                processQueue(null, null);
                isRefreshing = false;
                // Retry the original request
                return instance(originalRequest);
              }
            }
          } catch (refreshError: any) {
            if (refreshError?.response?.status === 401) {
              handleSessionExpired();
              logout();
              return new Promise(() => {});
            }
            processQueue(refreshError, null);
            isRefreshing = false;
            handleSessionExpired();
            logout();
            return new Promise(() => {}); // prevent downstream .catch() handlers from running after redirect
          }
        } else {
          logout();
        }
      }
      return Promise.reject(error);
    }
  );

  const getDelegation = async (url: string, delegation?: any) => {
    setError(null);

    try {
      const response = await instance.get(url, {
        headers: {
          ...(delegation ? { delegation: JSON.stringify(delegation) } : {})
        },
      }).then((res: any) => {
        const { status } = res;
        if (status && [200, 204].includes(status)) {
          return res;
        }
      });

      return response;
    } catch (err: any) {
      setError(err.response ? err.response.data : err.message);
      throw err;
    } finally {
      // if (!skipSpinner) setLoading(false);
    }
  };

  const get = async (url: string, options?: { delegation?: any; skipSpinner?: boolean }) => {
    const { delegation, skipSpinner = false } = options || {};
    
    if (!skipSpinner) setLoading(true);
    setError(null);

    try {
      const response = await instance.get(url, {
        headers: {
          ...(delegation ? { delegation: JSON.stringify(delegation) } : {})
        },
      }).then((res: any) => {
        const { status } = res;
        if (status && [200, 204].includes(status)) {
          return res;
        }
      });

      return response;
    } catch (err: any) {
      setError(err.response ? err.response.data : err.message);
      throw err;
    } finally {
      if (!skipSpinner) setLoading(false);
    }
  };

  const getArrayBuffer = async (url: any, delegation?: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await instance.get(url, {
        responseType: 'blob',
        headers: {
          ...(delegation ? { delegation: JSON.stringify(delegation) } : {})
        },
      }).then((res: any) => {
        const { status } = res;
        if (status && [200, 204].includes(status)) {
          return res;
        }
      });

      return response;
    } catch (err: any) {
      setError(err.response ? err.response.data : err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const post = async (url: any, data?: any) => {
    setLoading(true);
    setError(null);

    try {
      const response = await instance.post(url, data && data).then((res: any) => {
        const { status } = res;
        if (status && [200, 204].includes(status)) {
          return res;
        }
      });

      return response;
    } catch (err: any) {
      setError(err.response ? err.response.data : err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const postArrayBuffer = async (url: any, data: any) => {
    setLoading(true);
    setError(null);

    try {
      const response = await instance.post(url, data, {
        responseType: 'blob',
      }).then((res: any) => {
        const { status } = res;
        if (status && [200, 204].includes(status)) {
          return res;
        }
      });

      return response;
    } catch (err: any) {
      setError(err.response ? err.response.data : err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const postMultipart = async (url: any, data: any, delegation?: string, extraHeaderParam?: any) => {
    setLoading(true);
    setError(null);

    try {
      const response = await instance.post(url, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(delegation ? { delegation: JSON.stringify(delegation) } : {}),
          ...(extraHeaderParam && extraHeaderParam)
        }
      }).then((res: any) => {
        const { status } = res;
        if (status && [200, 204].includes(status)) {
          return res;
        }
      });

      return response;
    } catch (err: any) {
      setError(err.response ? err.response.data : err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const put = async (url: any, data: any) => {
    setLoading(false);
    setError(null);

    try {
      const response = await instance.put(url, data).then((res: any) => {
        const { status } = res;
        if (status && [200, 204].includes(status)) {
          return res;
        }
      });

      return response;
    } catch (err: any) {
      setError(err.response ? err.response.data : err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteRequest = async (url: any) => {
    setLoading(false);
    setError(null);

    try {
      const response = await instance.delete(url).then((res: any) => {
        const { status } = res;
        if (status && [200, 204].includes(status)) {
          return res;
        }
      });

      return response;
    } catch (err: any) {
      setError(err.response ? err.response.data : err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { get, getDelegation, getArrayBuffer, post, postArrayBuffer, postMultipart, put, deleteRequest, apiLoading, apiError, getSourceType };
};

export default useAxios;
